import {
  useMemo,
  useRef,
  useCallback,
  useState,
  useId,
  useEffect,
  type CSSProperties,
  type ReactNode,
} from 'react'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type SortingState,
  type OnChangeFn,
  type ColumnDef as TanStackColumnDef,
  type ColumnOrderState,
  type ColumnSizingState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'
import { renderCell } from './cells'
import { ColumnHeaderContent } from './column-header-content'
import { DataTableHeader } from './data-table-header'
import { TableState } from './table-state'
import { useTableScroll } from '@/hooks/use-table-scroll'
import type { ColumnDef } from './types'

/** Minimum column width in pixels */
const MIN_COLUMN_WIDTH = 60

/** A column at the minimum width is holding a control, not text. */
function SkeletonCell({ width, filled }: { width: number; filled?: boolean }) {
  if (width <= MIN_COLUMN_WIDTH) {
    return <Skeleton className="size-4" />
  }

  if (filled) {
    return <Skeleton className="h-4 w-full" />
  }

  return (
    <Skeleton
      className="h-4"
      style={{ width: Math.round((width - 24) * 0.6) }}
    />
  )
}
/** Default column width in pixels */
const DEFAULT_COLUMN_WIDTH = 150
/** Skeleton columns shown before the real columns are known */
const SKELETON_PLACEHOLDER_COLUMNS = 6
/** Estimated row height used by virtualizer */
const VIRTUAL_ROW_ESTIMATE_SIZE = 44
/** Number of rows to render outside viewport for smoother scrolling */
const VIRTUAL_ROW_OVERSCAN = 8
/** Column ID that stays pinned on horizontal scroll */
const STICKY_COLUMN_ID = 'displayName'

/** Returns CSS positioning styles to pin a column on horizontal scroll.
 *  Width/minWidth are intentionally NOT set here — callers handle sizing
 *  with their own MIN_COLUMN_WIDTH clamp so it isn't accidentally overridden. */
function getStickyStyles(
  isSticky: boolean,
  type: 'header' | 'cell'
): CSSProperties {
  if (!isSticky) return {}

  if (type === 'header') {
    return {
      position: 'sticky',
      left: 0,
      // -1px compensates for the border-top so the header doesn't leave a gap when stuck
      top: -1,
      zIndex: 30,
    }
  }

  return {
    position: 'sticky',
    left: 0,
    zIndex: 5,
  }
}

interface DataTableProps<TData extends Record<string, unknown>> {
  /** Table data rows */
  data: TData[]
  /** Column definitions with field metadata */
  columns: ColumnDef<TData>[]
  /** Whether initial data is loading */
  isLoading?: boolean
  /** Whether data failed to load */
  isError?: boolean
  /** Whether more data is being loaded */
  isLoadingMore?: boolean
  /** Whether there is more data to load */
  hasMore?: boolean
  /** Current sorting state */
  sorting?: SortingState
  /** Callback when sorting changes */
  onSortChange?: (sorting: SortingState) => void
  /** Current column order */
  columnOrder?: string[]
  /** Callback when column order changes */
  onColumnOrderChange?: (columnOrder: string[]) => void
  /** Current column sizing */
  columnSizing?: ColumnSizingState
  /** Callback when column sizes change */
  onColumnSizingChange?: (columnId: string, width: number) => void
  /** Callback to load more data */
  onLoadMore?: () => void
  /** Callback to refresh data (used in empty state) */
  onRefresh?: () => void
  /**
   * What the empty state says, and what it offers instead of Refresh.
   *
   * Opt-in, because the default — "No data available yet", refresh to look
   * again — is only true when nothing has been narrowed. A table emptied by its
   * own filters tells a reviewer to wait for data that is already there, and
   * offers the one action that cannot help.
   */
  emptyTitle?: string
  emptyDescription?: string
  emptyActionLabel?: string
  onEmptyAction?: () => void
  /** Callback when a row is clicked */
  onRowClick?: (row: TData) => void

  /**
   * Extra classes for one row, by its data.
   *
   * For rows that are present but not yet actionable. Without it a table can
   * only say so inside every cell it renders, which scatters one rule across
   * the whole column set.
   */
  rowClassName?: (row: TData) => string | undefined

  /**
   * Whether this particular row responds to a click.
   *
   * `enableRowClick` turns the behaviour on for the table; this excuses a row
   * that has nothing to open yet. Rows it returns false for lose the pointer
   * cursor too, so they do not invite the click they would ignore.
   */
  isRowClickable?: (row: TData) => boolean

  /**
   * Replaces the tail of one row's cells with a single spanning cell.
   *
   * For a row whose remaining columns have nothing to show yet and nothing to
   * act on: a lead the pipeline is still reading has no figures, no sector and
   * no decision to record, and repeating that across ten narrow columns says it
   * ten times in the least space each. Returning null leaves the row alone.
   *
   * Columns up to and including `columnId` render normally, so a row keeps
   * whatever identifies it.
   */
  spanFrom?: (row: TData) => { columnId: string; content: ReactNode } | null
  /**
   * Whether a click anywhere in a row invokes `onRowClick`.
   *
   * Off by default, because every other table in the app wires `onRowClick` to
   * the display-name link alone and a whole-row target there would be a
   * behaviour change nobody asked for. Deal Review opts in: a reviewer works
   * down a long list and aiming at a short piece of text on every row is the
   * friction (GS-144).
   *
   * A row that opts in still needs its own controls to keep working, so a cell
   * holding one stops the click from reaching the row.
   */
  enableRowClick?: boolean
  /** Search term for highlighting */
  searchTerm?: string
  /** Whether columns can be reordered */
  enableColumnReorder?: boolean
  /** Whether columns can be resized */
  enableColumnResize?: boolean
  /** Optional class name */
  className?: string
}

function DataTable<TData extends Record<string, unknown>>({
  data,
  columns,
  isLoading = false,
  isError = false,
  isLoadingMore = false,
  hasMore = false,
  sorting = [],
  onSortChange,
  columnOrder: externalColumnOrder,
  onColumnOrderChange,
  columnSizing: externalColumnSizing,
  onColumnSizingChange,
  onLoadMore,
  onRefresh,
  emptyTitle,
  emptyDescription,
  emptyActionLabel,
  onEmptyAction,
  onRowClick,
  rowClassName,
  isRowClickable,
  spanFrom,
  enableRowClick = false,
  searchTerm = '',
  enableColumnReorder = true,
  enableColumnResize = true,
  className,
}: DataTableProps<TData>) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const dndContextId = useId()

  // Ref for debouncing resize callbacks
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Internal column order state (used if not controlled externally)
  const [internalColumnOrder, setInternalColumnOrder] =
    useState<ColumnOrderState>(() => columns.map((col) => col.id))

  // Internal column sizing state - always used for smooth resize animation
  // Synced with external state when not actively resizing
  const [internalColumnSizing, setInternalColumnSizing] =
    useState<ColumnSizingState>(() => externalColumnSizing ?? {})

  // Track if we're currently resizing to prevent external state from overriding
  const isResizingRef = useRef(false)

  // Sync internal state with external when external changes (and not resizing)
  useEffect(() => {
    if (!isResizingRef.current && externalColumnSizing) {
      setInternalColumnSizing(externalColumnSizing)
    }
  }, [externalColumnSizing])

  // Use external or internal column order
  const columnOrder = externalColumnOrder ?? internalColumnOrder
  const setColumnOrder = onColumnOrderChange ?? setInternalColumnOrder

  // Always use internal sizing for smooth animation
  const columnSizing = internalColumnSizing

  // Build TanStack Table columns from our column definitions
  const tableColumns = useMemo((): TanStackColumnDef<TData, unknown>[] => {
    return columns.map((col) => ({
      id: col.id,
      // Use accessorFn to avoid TanStack's path-parsing warnings for nested fields
      accessorFn: col.accessorFn,
      header:
        typeof col.header === 'function'
          ? col.header
          : () => (
              <ColumnHeaderContent
                displayName={col.displayName}
                meta={col.meta}
              />
            ),
      cell: col.cell ?? renderCell,
      enableSorting: col.isSortable,
      enableResizing: enableColumnResize,
      meta: col.meta,
      size: col.width ?? DEFAULT_COLUMN_WIDTH,
      minSize: MIN_COLUMN_WIDTH,
    }))
  }, [columns, enableColumnResize])

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor)
  )

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      if (!onSortChange) return
      const newSorting =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(sorting)
          : updaterOrValue
      onSortChange(newSorting)
    },
    [onSortChange, sorting]
  )

  const handleColumnOrderChange: OnChangeFn<ColumnOrderState> = useCallback(
    (updaterOrValue) => {
      const newOrder =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(columnOrder)
          : updaterOrValue
      setColumnOrder(newOrder)
    },
    [columnOrder, setColumnOrder]
  )

  // Track the sizing state at the start of a resize operation
  const resizeStartSizingRef = useRef<ColumnSizingState>({})

  // Handle column sizing changes with debounced callback
  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = useCallback(
    (updaterOrValue) => {
      const newSizing =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(columnSizing)
          : updaterOrValue

      // Capture the starting state when resize begins
      if (!isResizingRef.current) {
        resizeStartSizingRef.current = { ...columnSizing }
      }

      // Mark as resizing to prevent external state sync
      isResizingRef.current = true

      // Update internal state immediately for smooth UI
      setInternalColumnSizing(newSizing)

      // Debounce the external callback to avoid too many API calls
      if (onColumnSizingChange) {
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current)
        }

        // Capture values for the closure
        const startSizing = resizeStartSizingRef.current

        resizeTimeoutRef.current = setTimeout(() => {
          // Find which columns changed from the start of the resize operation
          for (const [columnId, width] of Object.entries(newSizing)) {
            if (startSizing[columnId] !== width) {
              onColumnSizingChange(columnId, width)
            }
          }
          // Mark resizing as complete after callback
          isResizingRef.current = false
        }, 300)
      } else {
        // If no external callback, just mark resizing complete after a delay
        setTimeout(() => {
          isResizingRef.current = false
        }, 300)
      }
    },
    [columnSizing, onColumnSizingChange]
  )

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      columnOrder,
      columnSizing,
    },
    onSortingChange: handleSortingChange,
    onColumnOrderChange: handleColumnOrderChange,
    onColumnSizingChange: handleColumnSizingChange,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableSortingRemoval: true,
    enableColumnResizing: enableColumnResize,
    columnResizeMode: 'onChange',
    meta: {
      searchTerm,
      onRowClick,
      rowClassName,
      isRowClickable,
    },
  })
  const rows = table.getRowModel().rows
  const visibleColumnCount = table.getVisibleLeafColumns().length

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => VIRTUAL_ROW_ESTIMATE_SIZE,
    overscan: VIRTUAL_ROW_OVERSCAN,
  })

  const virtualRows = rowVirtualizer.getVirtualItems()
  const totalSize = rowVirtualizer.getTotalSize()
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - virtualRows[virtualRows.length - 1].end
      : 0

  // Handle drag end for column reordering
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (over && active.id !== over.id) {
        // Prevent moving the sticky column or dropping onto it
        if (active.id === STICKY_COLUMN_ID || over.id === STICKY_COLUMN_ID)
          return

        const oldIndex = columnOrder.indexOf(active.id as string)
        const newIndex = columnOrder.indexOf(over.id as string)
        const newOrder = arrayMove(columnOrder, oldIndex, newIndex)
        setColumnOrder(newOrder)
      }
    },
    [columnOrder, setColumnOrder]
  )

  // Handle scroll state and infinite loading
  const { handleScroll } = useTableScroll({
    scrollContainerRef,
    hasMore,
    isLoadingMore,
    onLoadMore,
  })

  // Loading skeleton
  if (isLoading) {
    const headers = table.getHeaderGroups()[0]?.headers ?? []
    // On a first load with nothing cached the columns come from the view,
    // which has not arrived yet. Placeholder columns keep the skeleton visible.
    const skeletonColumns =
      headers.length > 0
        ? headers.map((header) => ({
            id: header.id,
            width: Math.max(header.getSize(), MIN_COLUMN_WIDTH),
          }))
        : Array.from({ length: SKELETON_PLACEHOLDER_COLUMNS }, (_, i) => ({
            id: `placeholder-${i}`,
            width: DEFAULT_COLUMN_WIDTH,
          }))

    return (
      <div className={cn('rounded-md overflow-hidden', className)}>
        <Table disableWrapper className="min-w-full">
          <TableHeader>
            <TableRow>
              {skeletonColumns.map(({ id, width }, i) => {
                return (
                  <TableHead
                    key={id}
                    className={cn(
                      'border-t',
                      i === 0 && 'rounded-tl-sm',
                      i === skeletonColumns.length - 1 && 'rounded-tr-sm'
                    )}
                    style={{
                      width,
                      minWidth: MIN_COLUMN_WIDTH,
                      background:
                        'color-mix(in srgb, var(--primary) 8%, var(--background))',
                    }}
                  >
                    <SkeletonCell width={width} />
                  </TableHead>
                )
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 10 }).map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                {skeletonColumns.map(({ id, width }) => {
                  return (
                    <TableCell
                      key={id}
                      style={{ width, minWidth: MIN_COLUMN_WIDTH }}
                    >
                      <SkeletonCell width={width} filled />
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  // Error state with skeleton rows and overlay card
  if (isError) {
    return (
      <TableState
        columns={columns}
        table={table}
        className={className}
        title="Failed to load data"
        description="Something went wrong. Please try again."
        buttonText="Try Again"
        onClickButton={onRefresh}
      />
    )
  }

  // Empty state with skeleton rows and overlay card
  if (data.length === 0) {
    return (
      <TableState
        columns={columns}
        table={table}
        className={className}
        title={emptyTitle}
        description={emptyDescription}
        buttonText={emptyActionLabel}
        onClickButton={onEmptyAction ?? onRefresh}
      />
    )
  }

  return (
    <div className={cn('w-full flex-1 flex flex-col min-h-0', className)}>
      <div className="rounded-md relative flex-1 flex flex-col min-h-0">
        <DndContext
          id={dndContextId}
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={handleDragEnd}
        >
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="relative overflow-x-auto overflow-y-auto flex-1 h-full pr-4 md:pr-8 pb-16"
            style={{
              maxHeight: 'calc(100vh - 200px)',
            }}
          >
            <Table disableWrapper className="w-full">
              <TableHeader className="sticky top-0 z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="hover:bg-transparent"
                  >
                    <SortableContext
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map((header) => {
                        const isSticky = header.id === STICKY_COLUMN_ID
                        return (
                          <DataTableHeader
                            key={header.id}
                            header={header}
                            isDraggable={enableColumnReorder}
                            isResizable={enableColumnResize}
                            isSticky={isSticky}
                            stickyStyle={getStickyStyles(isSticky, 'header')}
                          />
                        )
                      })}
                    </SortableContext>
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {paddingTop > 0 && (
                  <TableRow aria-hidden="true" className="hover:bg-transparent">
                    <TableCell
                      colSpan={visibleColumnCount}
                      className="p-0 border-0"
                      style={{ height: `${paddingTop}px` }}
                    />
                  </TableRow>
                )}

                {virtualRows.map((virtualRow) => {
                  const row = rows[virtualRow.index]
                  if (!row) return null

                  const rowIsClickable =
                    enableRowClick &&
                    Boolean(onRowClick) &&
                    (isRowClickable?.(row.original) ?? true)

                  const cells = row.getVisibleCells()
                  const span = spanFrom?.(row.original) ?? null
                  const spanAt = span
                    ? cells.findIndex(
                        (cell) => cell.column.id === span.columnId
                      )
                    : -1
                  // Nothing to span unless the column was found and something
                  // follows it. An unknown column id leaves the row intact
                  // rather than swallowing all of it.
                  const spanning = spanAt !== -1 && spanAt < cells.length - 1
                  const shown = spanning ? cells.slice(0, spanAt + 1) : cells

                  return (
                    <TableRow
                      key={row.id}
                      data-index={virtualRow.index}
                      ref={rowVirtualizer.measureElement}
                      data-state={row.getIsSelected() && 'selected'}
                      className={cn(
                        rowIsClickable && 'cursor-pointer',
                        rowClassName?.(row.original)
                      )}
                      onClick={
                        rowIsClickable
                          ? () => onRowClick?.(row.original)
                          : undefined
                      }
                    >
                      {shown.map((cell) => {
                        const isSticky = cell.column.id === STICKY_COLUMN_ID
                        return (
                          <TableCell
                            key={cell.id}
                            className={cn(isSticky && 'bg-background')}
                            style={{
                              width: Math.max(
                                cell.column.getSize(),
                                MIN_COLUMN_WIDTH
                              ),
                              minWidth: MIN_COLUMN_WIDTH,
                              ...getStickyStyles(isSticky, 'cell'),
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        )
                      })}
                      {spanning && span && (
                        <TableCell colSpan={cells.length - shown.length}>
                          {span.content}
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}

                {paddingBottom > 0 && (
                  <TableRow aria-hidden="true" className="hover:bg-transparent">
                    <TableCell
                      colSpan={visibleColumnCount}
                      className="p-0 border-0"
                      style={{ height: `${paddingBottom}px` }}
                    />
                  </TableRow>
                )}

                {/* Loading more indicator */}
                {isLoadingMore && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={columns.length}
                      className="h-12 text-center"
                    >
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading more...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DndContext>
      </div>
    </div>
  )
}

// Wrapper for proper generic typing
function MemoizedDataTable<TData extends Record<string, unknown>>(
  props: DataTableProps<TData>
) {
  return <DataTable {...props} />
}

export { MemoizedDataTable as DataTable }
export type { DataTableProps }
